import classnames from 'classnames';
import React, { useEffect, useRef, useState } from 'react';
import type MediaQuery from 'react-responsive';
import { Localizer } from 'services/Localizer';
import AssetDownloadIcon from 'shared/assets/AssetDownloadIcon';
import AssetIcon from 'shared/assets/AssetIcon';
import ExternalMediaIFrame from 'shared/assets/ExternalMediaIFrame';
import LazyImage from 'shared/assets/LazyImage';
import { AssetType } from 'shared/assets/types';
import DownChevronSVG from 'shared/components/svg/DownChevronSVG';
import EnlargeSVG from 'shared/components/svg/EnlargeSVG';
import LeftChevronSVG from 'shared/components/svg/LeftChevronSVG';
import RightChevronSVG from 'shared/components/svg/RightChevronSVG';
import UpChevronSVG from 'shared/components/svg/UpChevronSVG';
import useSmoothScrollingPolyfill from 'shared/hooks/useSmoothScrollingPolyfill';
import ProductImageModal from './ProductImageModal';
import { ProductAsset } from './types';
import Spinner from 'shared/components/Spinner';
const LazyMediaQuery = React.lazy<typeof MediaQuery>(() => import('react-responsive'));

interface ProductAssetsProps {
	assets?: ProductAsset[] | null;
}

export const productAssetPlaceholder: ProductAsset = {
	altText: 'Image not available',
	sortOrder: 1,
	thumbnailUrl: '/images/image-not-found-thumb.png',
	mainImageUrl: '/images/image-not-found.png',
	enlargedImageUrl: '/images/image-not-found.png',
	assetType: AssetType.Image,
	assetUrl: '/images/image-not-found.png',
	embeddedUrl: '',
};

/**
 * Returns the first asset from the set of provided assets or null if no default found
 */
function initMainProductAsset(assets?: ProductAsset[] | null): ProductAsset | null {
	if (assets == null || assets.length === 0) {
		return null;
	}
	const defaultAsset = assets
		.sort(compareAssets)
		.find((asset) => asset.assetType === AssetType.Image || asset.assetType === AssetType.YouTube);
	return defaultAsset || null;
}

/**
 * Returns list of alternative assets (including the main asset).
 */
function initAltAssets(assets?: ProductAsset[] | null): ProductAsset[] {
	if (!assets || assets === null || assets.length === 0) {
		return [productAssetPlaceholder];
	}
	return assets.sort(compareAssets);
}

const compareAssets = (a: ProductAsset, b: ProductAsset) => a.sortOrder - b.sortOrder;

/**
 * Displays assets for a product, including alternate assets, and handles swapping
 */
export default function ProductAssets(props: ProductAssetsProps) {
	const [mainAsset, setMainAsset] = useState(productAssetPlaceholder);
	const [altAssets, setAltAssets] = useState<ProductAsset[]>([]);
	const [isImageSliderModalOpen, setIsImageSliderModalOpen] = useState(false);

	const carousel = useRef<HTMLUListElement>(null);

	const nodes = new Map();
	useSmoothScrollingPolyfill();

	useEffect(() => {
		const newMainAsset = initMainProductAsset(props.assets);

		if (newMainAsset) {
			setMainAsset(newMainAsset);
		} else {
			setMainAsset(productAssetPlaceholder);
		}

		const initialThumbnails = initAltAssets(props.assets);
		setAltAssets(initialThumbnails);
	}, [props.assets]);

	/**
	 * Replaces the main image with the provided one
	 * @param e
	 * @param asset
	 */
	function changeMainImage(e: React.MouseEvent<HTMLButtonElement, MouseEvent>, asset: ProductAsset): void {
		e.preventDefault();
		setMainAsset(asset);
	}

	/**
	 * Scrolsl the carousel
	 *
	 * @param {boolean} up
	 * @returns
	 */
	function scrollCarousel(up: boolean) {
		if (Array.from(nodes.values()).length === 0) {
			return;
		}

		if (!carousel || !carousel.current) {
			return;
		}

		const item = Array.from(nodes.values()).filter((node) => node != null)[0];

		if (window.innerWidth > 767) {
			carousel.current.scrollBy(0, up ? -item.clientHeight : item.clientHeight);
		} else {
			carousel.current.scrollBy(up ? -item.clientHeight : item.clientHeight, 0);
		}
	}

	/**
	 * Handle previous button click
	 *
	 */
	function handlePreviousButton() {
		scrollCarousel(true);
	}

	/**
	 * Handle next button click
	 *
	 */
	function handleNextButton() {
		scrollCarousel(false);
	}

	const assetScroller = (
		<div className="roc-pdp-assets__scroller" data-testid="asset-scroller">
			<div className="roc-pdp-asset-scroller">
				<button
					type="button"
					onClick={handlePreviousButton}
					className="roc-pdp-asset-scroller__navigation roc-pdp-asset-scroller__navigation--up"
					aria-label="Previous"
				>
					<LazyMediaQuery maxWidth={767}>
						<LeftChevronSVG />
					</LazyMediaQuery>
					<LazyMediaQuery minWidth={768}>
						<UpChevronSVG />
					</LazyMediaQuery>
				</button>
				<ul ref={carousel} className="roc-pdp-asset-scroller__track">
					{altAssets.map((altAsset, i) => (
						<li
							key={altAsset.sortOrder}
							ref={(c) => nodes.set(i, c)}
							className="roc-pdp-asset-scroller__item"
						>
							{altAsset.assetType === AssetType.Image || altAsset.assetType === AssetType.YouTube ? (
								<button
									type="button"
									className={
										'roc-pdp-asset-scroller__button' +
										(altAsset.mainImageUrl === mainAsset.mainImageUrl
											? ' roc-pdp-asset-scroller__button--active'
											: '')
									}
									onClick={(event) => changeMainImage(event, altAsset)}
									aria-label="Select image"
								>
									{altAsset.assetType === AssetType.Image ? (
										<LazyImage
											alt={altAsset.altText}
											className="roc-pdp-asset-scroller__image"
											src={altAsset.thumbnailUrl}
										/>
									) : (
										<>
											<AssetIcon
												asset={altAsset}
												assetIconClasses={classnames(
													'roc-pdp-asset-scroller__image roc-pdp-asset-scroller__image--svg',
													{
														'roc-pdp-asset-scroller__image--svg-overlay':
															altAsset.assetType === AssetType.YouTube,
													},
												)}
											/>
											<LazyImage
												alt={altAsset.altText}
												className={classnames('roc-pdp-asset-scroller__image', {
													'roc-pdp-asset-scroller__image--overlay':
														altAsset.assetType === AssetType.YouTube,
												})}
												src={altAsset.thumbnailUrl}
											/>
										</>
									)}
								</button>
							) : (
								<a
									href={altAsset.assetUrl}
									download
									className="roc-pdp-asset-scroller__button"
									aria-label="Download asset"
								>
									<AssetDownloadIcon
										asset={altAsset}
										assetIconClasses="roc-pdp-asset-scroller__image roc-pdp-asset-scroller__image--svg"
										downloadIconClasses="roc-pdp-asset-scroller__button-download-icon"
									/>
								</a>
							)}
						</li>
					))}
				</ul>
				<button
					type="button"
					onClick={handleNextButton}
					className="roc-pdp-asset-scroller__navigation roc-pdp-asset-scroller__navigation--down"
					aria-label="Next"
				>
					<LazyMediaQuery maxWidth={767}>
						<RightChevronSVG />
					</LazyMediaQuery>
					<LazyMediaQuery minWidth={768}>
						<DownChevronSVG />
					</LazyMediaQuery>
				</button>
			</div>
		</div>
	);

	const mainImageContent = (
		<div className="roc-pdp-assets__main" data-testid="main-image-wrapper">
			{mainAsset.assetType !== AssetType.YouTube ? (
				<>
					<div className="roc-pdp-main-image__image-wrapper">
						<LazyImage
							src={mainAsset.mainImageUrl}
							alt={mainAsset.altText}
							className="roc-pdp-main-image__image"
						/>
					</div>
					<div className="roc-pdp-main-image__options">
						<div className="roc-pdp-main-image__options-item">
							<button
								type="button"
								className="roc-pdp-main-image__option-btn"
								onClick={() => setIsImageSliderModalOpen(true)}
							>
								<div className="roc-pdp-main-image__options-item-icon">
									<EnlargeSVG />
								</div>
								<div className="roc-pdp-main-image__options-item-text">
									<span>{Localizer('ViewLarger')}</span>
								</div>
							</button>
						</div>
					</div>
				</>
			) : (
				<ExternalMediaIFrame embeddedUrl={mainAsset.embeddedUrl} title={mainAsset.altText} />
			)}
		</div>
	);

	return (
		<React.Suspense fallback={<Spinner />}>
			<>
				{
					<ProductImageModal
						isOpen={isImageSliderModalOpen}
						closeModal={() => {
							setIsImageSliderModalOpen(false);
						}}
					>
						<img
							src={mainAsset.enlargedImageUrl}
							alt={mainAsset.altText}
							className="roc-pdp-main-image__fullscreen-image"
						/>
					</ProductImageModal>
				}
				<div className="roc-pdp-assets">
					<LazyMediaQuery maxWidth={767}>
						{mainImageContent}
						{assetScroller}
					</LazyMediaQuery>
					<LazyMediaQuery minWidth={768}>
						{assetScroller}
						{mainImageContent}
					</LazyMediaQuery>
				</div>
			</>
		</React.Suspense>
	);
}
